import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { CoinbaseEngUsersAccountManagementApiApiProductRoleDefinition } from '@cbhq/identity-api-hooks/account-management/types';

import { useOrganization } from '../../../contexts/OrganizationContext';
import type { FormatMessage } from '../components/InviteTray/productCatalog';
import { getProductDisplayName } from '../components/InviteTray/productCatalog';

import type { MemberRole, MemberRoleMap } from './constants';

export const EXCLUDED_PRODUCTS = new Set(['retail']);

export function getProductKey(rawRole: string) {
  const slashIndex = rawRole.indexOf('/');
  return slashIndex === -1 ? '' : rawRole.slice(0, slashIndex);
}

// Returns the default auto-selected role for a product, or an empty array if none exists.
export function getDefaultRoleValues(
  rolesByProduct: Record<string, MemberRoleMap[]>,
  productKey: string,
): MemberRole[] {
  const options = rolesByProduct[productKey];
  return options?.length ? [options[options.length - 1].value] : [];
}

/**
 * Transforms a `ProductRoleDefinition[]` (the shape carried by admin-entities
 * `filteredRoles`) into a roleMap keyed by full role ID
 * (`{product_id}/legal_entity:{role_id}`). Roles from EXCLUDED_PRODUCTS (e.g.
 * retail) are dropped. `assignable` defaults to true when the field is absent, so
 * only an explicit `false` marks a role as held-but-not-grantable.
 */
export function buildRolesFromProducts(
  products: CoinbaseEngUsersAccountManagementApiApiProductRoleDefinition[] | undefined,
  formatMessage: FormatMessage,
): Record<MemberRole, MemberRoleMap> {
  const roleMap: Record<MemberRole, MemberRoleMap> = {};

  if (products) {
    products.forEach((product) => {
      if (!product.id || !product.roles) {
        return;
      }

      if (EXCLUDED_PRODUCTS.has(product.id)) {
        return;
      }

      product.roles.forEach((role) => {
        if (!role.id || !product.id) {
          return;
        }
        const fullRoleId: MemberRole = `${product.id}/legal_entity:${role.id}`;
        if (roleMap[fullRoleId]) {
          return;
        }

        const productLabel = getProductDisplayName(
          product.id,
          formatMessage,
          product.name ?? product.id,
        );
        const name = role.name ?? role.id;

        roleMap[fullRoleId] = {
          value: fullRoleId,
          label: `${productLabel}: ${name}`,
          name,
          description: role.description || '',
          productKey: product.id,
          assignable: role.assignable !== false,
        };
      });
    });
  }

  return roleMap;
}

export type UseRolesReturn = {
  /**
   * Union of role definitions across every legal entity, first-occurrence-wins.
   * Includes unassignable roles so display paths can label a role a member holds
   * but can no longer be granted.
   */
  roleMap: Record<MemberRole, MemberRoleMap>;
  /** Union of all role IDs across every legal entity (inclusive of unassignable). */
  supportedRoles: MemberRole[];
  /** Per-entity roles grouped by product, assignable-only — the invite/edit picker surface. */
  rolesByProductPerEntity: Record<string, Record<string, MemberRoleMap[]>>;
  /** Flat list of every role definition (inclusive), for filter/dropdown call sites. */
  roleOptions: MemberRoleMap[];
  /** True when the role exists in the union roleMap (inclusive); excluded products never match. */
  hasRoleDefinition: (role: string) => boolean;
};

/**
 * Derives available roles from admin-entities' per-entity `filteredRoles`
 * (already loaded into OrganizationContext) instead of the org-wide
 * GetRoleDefinitions endpoint. `roleMap` / `hasRoleDefinition` / `roleOptions`
 * stay inclusive of unassignable roles for display and filtering; only the
 * picker surface (`rolesByProductPerEntity`) is filtered to assignable roles.
 */
export function useRoles(): UseRolesReturn {
  const { formatMessage } = useIntl();
  const { selectedOrganization } = useOrganization();
  const legalEntities = selectedOrganization?.legalEntities;

  return useMemo(() => {
    const rolesByProductPerEntity: Record<string, Record<string, MemberRoleMap[]>> = {};
    const roleMap: Record<MemberRole, MemberRoleMap> = {};
    const supportedRoles: MemberRole[] = [];

    if (legalEntities) {
      for (const le of legalEntities) {
        if (le.filteredRoles) {
          const leRoleMap = buildRolesFromProducts(le.filteredRoles, formatMessage);

          // Pickers offer assignable roles only; unassignable held roles are
          // still surfaced for display via roleMap.
          const leRolesByProduct: Record<string, MemberRoleMap[]> = {};
          for (const option of Object.values(leRoleMap)) {
            if (option.assignable) {
              (leRolesByProduct[option.productKey] ??= []).push(option);
            }
          }
          rolesByProductPerEntity[le.legalEntityId] = leRolesByProduct;

          // Union across entities, first-occurrence-wins.
          for (const [key, value] of Object.entries(leRoleMap)) {
            if (!roleMap[key]) {
              roleMap[key] = value;
              supportedRoles.push(key);
            }
          }
        } else {
          rolesByProductPerEntity[le.legalEntityId] = {};
        }
      }
    }

    const roleOptions = Object.values(roleMap);
    const hasRoleDefinition = (role: string) => roleMap[role as MemberRole] !== undefined;

    return {
      roleMap,
      supportedRoles,
      rolesByProductPerEntity,
      roleOptions,
      hasRoleDefinition,
    };
  }, [formatMessage, legalEntities]);
}
